import React, { useState, useEffect } from 'react';
import { 
  Box, Typography, Card, Table, TableBody, TableCell, TableContainer, 
  TableHead, TableRow, Chip, TableSortLabel
} from '@mui/material';
import api from '../../utils/axios';
import { toast } from 'react-toastify';
import { getComparator, stableSort } from '../../utils/sortUtils';

const ManageSubscriptions = () => {
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);

  // Sorting State
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('firstName');

  useEffect(() => {
    fetchSubscriptions();
  }, []);

  const fetchSubscriptions = async () => {
    try {
      const res = await api.get('/superadmin/subscriptions');
      if (res.data.success) {
        setSubscriptions(res.data.data);
      }
    } catch (err) {
      toast.error('Failed to load subscriptions');
    } finally {
      setLoading(false);
    }
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const getSourceColor = (source) => {
    switch(source) {
      case 'stripe': return 'secondary';
      case 'apple': return 'info';
      case 'google': return 'success';
      case 'manual': return 'warning';
      default: return 'default';
    }
  };

  const sortedSubscriptions = stableSort(subscriptions, getComparator(order, orderBy));

  return (
    <Box>
      <Typography variant="h4" gutterBottom>Subscription Management</Typography>
      <Typography variant="body1" color="text.secondary" mb={3}>
        Overview of users with active PRO status or existing billing profiles.
      </Typography>
      
      <Card sx={{ mt: 3, overflow: 'hidden' }}>
        <TableContainer sx={{ maxHeight: '70vh' }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'firstName'}
                    direction={orderBy === 'firstName' ? order : 'asc'}
                    onClick={() => handleRequestSort('firstName')}
                  >
                    User
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'church.name'}
                    direction={orderBy === 'church.name' ? order : 'asc'}
                    onClick={() => handleRequestSort('church.name')}
                  >
                    Church
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'isPro'}
                    direction={orderBy === 'isPro' ? order : 'asc'}
                    onClick={() => handleRequestSort('isPro')}
                  >
                    PRO Status
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'proSource'}
                    direction={orderBy === 'proSource' ? order : 'asc'}
                    onClick={() => handleRequestSort('proSource')}
                  >
                    Source
                  </TableSortLabel>
                </TableCell>
                <TableCell>Expires At</TableCell>
                <TableCell>Transaction ID</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedSubscriptions.map((sub) => (
                <TableRow key={sub._id} hover>
                  <TableCell>
                    {sub.firstName} {sub.lastName}
                    <Typography variant="caption" display="block" color="text.secondary">
                      {sub.email}
                    </Typography>
                  </TableCell>
                  <TableCell>{sub.church?.name || 'N/A'}</TableCell>
                  <TableCell>
                    {sub.isPro ? (
                      <Chip label="Active" color="success" size="small" />
                    ) : (
                      <Chip label="Inactive" color="default" size="small" />
                    )}
                  </TableCell>
                  <TableCell>
                    {sub.proSource ? (
                      <Chip label={sub.proSource.toUpperCase()} color={getSourceColor(sub.proSource)} size="small" />
                    ) : '-'}
                  </TableCell>
                  <TableCell>
                    {sub.proExpiresAt ? new Date(sub.proExpiresAt).toLocaleDateString() : 'Never'}
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                      {sub.proTransactionId || sub.subscriptionId || '-'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
              {subscriptions.length === 0 && !loading && (
                <TableRow><TableCell colSpan={6} align="center">No subscriptions found</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>
    </Box>
  );
};

export default ManageSubscriptions;
